import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";

type TokenType = "" | "NAU" | "NAUY" | "NAUN";

interface RawTx {
  date: string;
  NAUBurn?: number;
  NAUYMint?: number;
  NAUNMint?: number;
  [key: string]: unknown;
}

interface ExplorerSectionProps {
  users: string[];
  selectedUser: string;
  setSelectedUser: (user: string) => void;
  data: RawTx[];
  loading: boolean;
  token?: TokenType;
}

interface ChartItem {
  date: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

const linesConfig = [
  { key: "NAUBurn", stroke: "#000000", name: "🔥 NAU Burn" },
  { key: "NAUYMint", stroke: "#1d4ed8", name: "🔵 NAUY Mint" },
  { key: "NAUNMint", stroke: "#b91c1c", name: "🔴 NAUN Mint" },
] as const;

function transformUserTxs(data: RawTx[]): ChartItem[] {
  return data.map((tx) => {
    let date = tx.date;
    try {
      date = format(parseISO(tx.date), "yyyy-MM-dd");
    } catch {
      // ignore parse errors
    }
    return {
      date,
      NAUBurn: tx.NAUBurn ?? 0,
      NAUYMint: tx.NAUYMint ?? 0,
      NAUNMint: tx.NAUNMint ?? 0,
    };
  });
}

const ExplorerSection: React.FC<ExplorerSectionProps> = ({
  users,
  selectedUser,
  setSelectedUser,
  data,
  loading,
  token = "",
}) => {
  const chartData = useMemo(() => transformUserTxs(data), [data]);

  const totals = useMemo(
    () => ({
      NAU: chartData.reduce((sum, item) => sum + item.NAUBurn, 0),
      NAUY: chartData.reduce((sum, item) => sum + item.NAUYMint, 0),
      NAUN: chartData.reduce((sum, item) => sum + item.NAUNMint, 0),
    }),
    [chartData]
  );

  const filteredLines = useMemo(
    () =>
      linesConfig
        .filter(
          (line) =>
            token === "" || line.key.toLowerCase().includes(token.toLowerCase())
        )
        .map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            stroke={line.stroke}
            dot={{ r: 3 }}
            name={line.name}
          />
        )),
    [token]
  );

  return (
    <div className="w-full flex flex-col gap-6">
      <h2
        id="explorer-section"
        className="text-4xl font-bold mb-10 text-center text-red-800"
      >
        Explorer Section
      </h2>

      <div className="flex-1 bg-white rounded-lg shadow p-6 flex items-center">
        <p className="text-gray-700 text-sm leading-relaxed">
          Dive into individual participation data. This view reveals how users
          interact with the protocol — from burning to minting{" "}
          <strong>NAUY</strong> or <strong>NAUN</strong>. Use it to analyze
          sentiment clusters, active wallet behavior, or ecosystem health over
          time.
        </p>
      </div>

      {/* User Selector */}
      <label className="flex flex-col md:flex-row items-center gap-4">
        <span className="font-semibold text-[#72a9e3]">SELECT A USER:</span>
        <select
          value={selectedUser}
          onChange={(e) => setSelectedUser(e.target.value)}
          className="p-2 border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-600 text-[#333] w-full md:w-auto"
        >
          <option value="">-- Choose a user address --</option>
          {users.length === 0 ? (
            <option disabled>No users available</option>
          ) : (
            users.map((user) => (
              <option key={user} value={user}>
                {user.slice(0, 6)}...{user.slice(-4)}
              </option>
            ))
          )}
        </select>
      </label>

      {/* Totals Display */}
      {selectedUser && (
        <motion.div
          role="region"
          aria-labelledby="totals-title"
          className="w-full bg-white/90 p-6 rounded shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h3
            id="totals-title"
            className="text-xl font-bold text-gray-800 mb-4 text-center"
          >
            User Address: {selectedUser}
          </h3>
          <div className="flex flex-col sm:flex-row sm:justify-around gap-3">
            <p className="text-black">
              🔥 Total NAU Burn:{" "}
              <span className="font-mono">{totals.NAU.toFixed(4)}</span>
            </p>
            <p className="text-blue-700">
              🔵 Total NAUY Mint:{" "}
              <span className="font-mono">{totals.NAUY.toFixed(4)}</span>
            </p>
            <p className="text-red-700">
              🔴 Total NAUN Mint:{" "}
              <span className="font-mono">{totals.NAUN.toFixed(4)}</span>
            </p>
          </div>
        </motion.div>
      )}

      {/* Chart or Messages */}
      {loading ? (
        <p className="text-center text-blue-600 animate-pulse">
          ⏳ Loading transactions...
        </p>
      ) : !selectedUser ? (
        <p className="text-center text-gray-400 italic">
          Please select a user to view transactions.
        </p>
      ) : chartData.length === 0 ? (
        <p className="text-center text-red-600">
          ❌ No transactions found for this user.
        </p>
      ) : (
        <motion.div
          role="region"
          aria-labelledby="chart-title"
          className="w-full bg-white/90 p-6 rounded shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h3
            id="chart-title"
            className="text-2xl font-bold text-center text-gray-800 mb-4"
          >
            Transaction Activity
          </h3>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, bottom: 10, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="date"
                stroke="#333"
                tickFormatter={(iso) => format(parseISO(iso), "dd/MM")}
              />
              <YAxis stroke="#333" />
              <Tooltip />
              <Legend verticalAlign="top" height={36} />
              {filteredLines}
            </LineChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </div>
  );
};

export default ExplorerSection;
